/**
 * Meta (Facebook) Pixel standard events.
 *
 * The pixel snippet itself is per-tenant config, pasted in FMS → Website
 * Settings and injected by `analytics/marketingTags`, so `fbq` exists only on
 * the storefronts whose operator added one. Everywhere else every call here
 * is a no-op.
 *
 * Purchase is not sent from here. The operator sets it in Meta's Event Setup
 * Tool as a URL rule on /payment-success, the page a card payment reaches
 * only once the charge went through; `booking_completed` stays a PostHog
 * event. That rule counts each opening of the page, so it is opened once
 * (leavePaymentSuccess). Never a click rule on the checkout button: a click is
 * not a payment, and a declined card or a missing field would still be counted
 * as a Purchase.
 *
 * The events below mirror the funnel events we already send, so Meta sees
 * them with their amounts. A card payment that did not go through is the
 * custom event PaymentFailed (1NOW-11860): for analysis, never a conversion.
 */

import { matchRoutes } from "react-router-dom";

/**
 * Meta's Purchase is the operator's URL rule on /payment-success, and the pixel
 * counts each time that address is opened: a load, and also Back, Forward and
 * a page the browser restores from its back-forward cache (fbevents.js wraps
 * pushState/replaceState and listens for popstate and pageshow). A card payment
 * opens it once, by the checkout's push, and leaving the page replaces it
 * (PaymentSuccess). Any other way onto it goes home before the router or the
 * pixel reads the address: index.tsx runs this at boot and on popstate and
 * pageshow, registered ahead of both. After a reload there is no booking in
 * memory to show anyway. A pay-later `?reserved=` page is no purchase and keeps
 * its reload (1NOW-11198). The address is matched the way the router matches
 * its routes (any case, extra trailing slashes, percent-encoding), so no
 * spelling that shows the success page gets past this.
 */
export const leavePaymentSuccess = (): void => {
  const { pathname, search } = window.location;
  if (matchRoutes([{ path: "/payment-success" }], pathname) && !new URLSearchParams(search).has("reserved"))
    window.history.replaceState(window.history.state, "", "/");
};

let activePixelId = "";

/** The pixel id we initialised from `meta_pixel_id`, or "" when none is set. */
export const getMetaPixelId = (): string => activePixelId;
export const setMetaPixelId = (id: string): void => {
  activePixelId = id;
};

let lastUserDataKey = "";

/**
 * Advanced matching (1NOW-11118): re-`init` the pixel with hashed-by-Meta
 * identifiers. Values go only to fbq, never to URLs or PostHog. Skipped when
 * there is no pixel id or the data has not changed.
 */
export function setMetaUserData(user: {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
}): void {
  const fbq = (window as any).fbq;
  if (!activePixelId || typeof fbq !== "function") return;
  const lower = (v?: string) => (v || "").trim().toLowerCase();
  const data: Record<string, string> = {
    em: lower(user.email),
    // react-phone-input-2 already yields digits with the country code
    ph: (user.phone || "").replace(/\D/g, ""),
    fn: lower(user.firstName),
    ln: lower(user.lastName),
  };
  Object.keys(data).forEach((k) => data[k] || delete data[k]);
  const key = activePixelId + JSON.stringify(data);
  if (!Object.keys(data).length || key === lastUserDataKey) return;
  try {
    fbq("init", activePixelId, data);
    lastUserDataKey = key;
  } catch (error) {
    console.error("[Meta] advanced matching failed", error);
  }
}

/** Our funnel event -> Meta standard event. Anything unmapped is not sent. */
const STANDARD_EVENTS: Record<string, string> = {
  vehicle_listing_viewed: "ViewContent",
  booking_started: "InitiateCheckout",
  vehicle_search: "Search",
  contact_clicked: "Contact",
};

/** Our event -> a Meta custom event (fbq trackCustom), which Meta never counts as a conversion. */
const CUSTOM_EVENTS: Record<string, string> = {
  payment_failed: "PaymentFailed",
};

const ID_PREFIX: Record<string, string> = {
  InitiateCheckout: "ic",
};

/**
 * 1NOW-11119: Meta dedupes browser vs server events on `eventID`. The booking
 * row uses `ic-<id>`, which the backend can reproduce.
 */
const eventId = (standard: string, bookingId: unknown): string => {
  const prefix = ID_PREFIX[standard];
  if (prefix && bookingId !== undefined && bookingId !== null && bookingId !== "")
    return `${prefix}-${bookingId}`;
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * 1NOW-11121: Meta `Search` carries the trip's dates as `content_category`,
 * "2026-10-20 to 2026-10-23" (pick-up date to return date), beside
 * `search_string` (the pick-up location). Takes the payload's own
 * "2026-10-20T10:00:00" strings, so it is the date the availability API got.
 */
export const searchDatesCategory = (start?: string | null, end?: string | null): string => {
  const day = (iso?: string | null) => String(iso ?? "").split("T")[0];
  return day(start) && day(end) ? `${day(start)} to ${day(end)}` : "";
};

export function trackMetaEvent(
  eventName: string,
  properties?: Record<string, any>
): void {
  const custom = CUSTOM_EVENTS[eventName];
  const name = STANDARD_EVENTS[eventName] ?? custom;
  const fbq = (window as any).fbq;
  if (!name || typeof fbq !== "function") return;

  const props = properties ?? {};
  const value = Number(props.value);
  const payload: Record<string, any> = {};

  if (props.vehicle_id) {
    payload.content_type = "product";
    payload.content_ids = [String(props.vehicle_id)];
  }
  if (props.vehicle_name) payload.content_name = props.vehicle_name;
  if (props.search_string) payload.search_string = String(props.search_string);
  if (props.content_category) payload.content_category = String(props.content_category);
  // PaymentFailed's own: which processor, and its code (card_declined, refused, ...).
  if (props.provider) payload.provider = String(props.provider);
  if (props.code) payload.code = String(props.code);
  // A missing or unusable amount still sends the event — Meta counts it, it
  // just cannot optimise on revenue. Sending `value: NaN` would be worse.
  if (Number.isFinite(value) && value > 0) {
    // `value` arrives as whatever the API sent — the price fields are DRF
    // decimals, i.e. strings like "394.00" — and `currency` is lowercase
    // ("usd") on /api/company/configurations/, where Meta wants ISO 4217.
    payload.value = value;
    payload.currency = String(props.currency || "USD").toUpperCase();
  }

  // `fbq` is the operator's own pasted snippet, and these calls sit inside the
  // booking funnel (InitiateCheckout fires as a booking is made). A broken
  // vendor snippet must cost us an analytics event, never the renter's booking.
  try {
    fbq(custom ? "trackCustom" : "track", name, payload, {
      eventID: eventId(name, props.booking_id),
    });
  } catch (error) {
    console.error("[Meta] pixel event failed", name, error);
  }
}
